import { grey } from '@mui/material/colors';
import { createTheme as createMuiTheme } from '@mui/material/styles';
import MuiCheckbox from '././components/MuiCheckbox';
import MuiAutocomplete from './components/MuiAutocomplete';
import MuiAvatar from './components/MuiAvatar';
import MuiAvatarGroup from './components/MuiAvatarGroup';
import MuiBackdrop from './components/MuiBackdrop';
import MuiButton from './components/MuiButton';
import MuiCardContent from './components/MuiCardContent';
import MuiCircularProgress from './components/MuiCircularProgress';
import MuiDialog from './components/MuiDialog';
import MuiDialogActions from './components/MuiDialogActions';
import MuiDialogContent from './components/MuiDialogContent';
import MuiDialogTitle from './components/MuiDialogTitle';
import MuiDivider from './components/MuiDivider';
import MuiFormControlLabel from './components/MuiFormControlLabel';
import MuiFormHelperText from './components/MuiFormHelperText';
import MuiInputAdornment from './components/MuiInputAdornment';
import MuiInputBase from './components/MuiInputBase';
import MuiList from './components/MuiList';
import MuiListItemButton from './components/MuiListItemButton';
import MuiListItemText from './components/MuiListItemText';
import MuiMenu from './components/MuiMenu';
import MuiMenuItem from './components/MuiMenuItem';
import MuiOutlinedInput from './components/MuiOutlinedInput';
import MuiPaper from './components/MuiPaper';
import MuiPopover from './components/MuiPopover';
import MuiRadio from './components/MuiRadio';
import MuiSelect from './components/MuiSelect';
import MuiStepIcon from './components/MuiStepIcon';
import MuiSvgIcon from './components/MuiSvgIcon';
import MuiSwitch from './components/MuiSwitch';
import MuiTab from './components/MuiTab';
import MuiTableCell from './components/MuiTableCell';
import MuiTablePagination from './components/MuiTablePagination';
import MuiTableSortLabel from './components/MuiTableSortLabel';
import MuiTabs from './components/MuiTabs';
import MuiTooltip from './components/MuiTooltip';
import MuiAccordion from './components/MuiAccordion';
import MuiAccordionDetails from './components/MuiAccordionDetails';
import MuiAccordionSummary from './components/MuiAccordionSummary';

import {
  defaultGreenSiteWhiteLabelConfig,
  defaultSubdomainWhiteLabelConfig,
  ThemeConfig,
} from './defaults';
import './types';

export const spacing = 4;
export const fontSizes = {
  sm: 12,
  regular: 14,
  lg: 16,
  xl: 20,
  xxl: 24,
};
export const spacingSizes = {
  xxs: 1,
  xs: 2,
  sm: 4,
  md: 5,
  lg: 6,
  xl: 8,
  xxl: 10,
  xxxl: 14,
};

export default function createTheme({
  whiteLabelConfig,
  isAdminSite,
  isSubdomain,
}: ThemeConfig) {
  const themeConfig = isAdminSite
    ? whiteLabelConfig
    : isSubdomain
    ? { ...defaultSubdomainWhiteLabelConfig, ...(whiteLabelConfig ?? {}) }
    : defaultGreenSiteWhiteLabelConfig;

  return createMuiTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    spacing,
    spacingSizes,
    shape: {
      borderRadius: 8,
    },
    palette: {
      mode: 'light',
      common: {
        black: '#000',
        white: '#FFF',
      },
      background: {
        default: '#F0F1EE',
        // eslint-disable-next-line
        primary: themeConfig?.bgColor || '#F0F1EE', // bgColor may be '' or ``
        paper: '#FFFFFF',
      },
      borderColor: {
        main: grey[300],
      },
      primary: {
        // eslint-disable-next-line
        main: themeConfig?.primaryColor || '#56B26C', // primaryColor may be '' or ``
        dark: '#4BA061',
        light: '#6DC986',
        // eslint-disable-next-line
        contrastText: themeConfig?.primaryContrastText || '#FFFFFF', // primaryContrastText may be '' or ``
      },
      secondary: {
        // eslint-disable-next-line
        main: themeConfig?.secondaryColor || '#2196F3', // secondaryColor may be '' or ``
        dark: '#1976D2',
        light: '#64B5F6',
        contrastText: '#FFFFFF',
      },
      error: {
        contrastText: '#FFFFFF',
        main: '#D32F2F',
        dark: '#C62828',
        light: '#B3261E',
        lightWarning: '#FEEBEE',
      },
      warning: {
        contrastText: '#FFFFFF',
        main: '#EF6C00',
        dark: '#E65100',
      },
      info: {
        contrastText: '#FFFFFF',
        main: '#0288D1',
        dark: '#01579B',
      },
      success: {
        main: '#347B49',
        dark: '#28593A',
        light: '#56B26C',
        contrastText: '#FFFFFF',
      },
      text: {
        primary: 'rgba(0, 0, 0, 0.87)',
      },
      action: {
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        hover: 'rgba(0, 0, 0, 0.04)',
      },
    },
    fontSizes,
    typography: (palette) => ({
      fontFamily: 'MaisonNeueNew',
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontSize: '3rem',
        fontWeight: 500,
        lineHeight: '150%',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 500,
        lineHeight: '100%',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 500,
        lineHeight: '150%',
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 500,
        lineHeight: '150%',
        color: palette.grey[900],
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '150%',
      },
      h6: {
        fontSize: '0.875rem',
        lineHeight: '150%',
        color: palette.grey[900],
      },
      body1: {
        lineHeight: '150%',
      },
      body2: {
        lineHeight: '150%',
        color: palette.grey[600],
      },
      subtitle1: {
        fontWeight: 500,
        lineHeight: '150%',
      },
      subtitle2: {
        lineHeight: '150%',
      },
      caption: {
        lineHeight: '12px',
        color: palette.grey[600],
      },
    }),
    components: {
      MuiBackdrop,
      MuiSelect,
      MuiInputBase,
      MuiButton,
      MuiTableCell,
      MuiDivider,
      MuiMenu,
      MuiMenuItem,
      MuiSvgIcon,
      MuiAvatar,
      MuiOutlinedInput,
      MuiTableSortLabel,
      MuiFormHelperText,
      MuiList,
      MuiListItemButton,
      MuiListItemText,
      MuiInputAdornment,
      MuiTab,
      MuiTabs,
      MuiCircularProgress,
      MuiRadio,
      MuiFormControlLabel,
      MuiCheckbox,
      MuiTooltip,
      MuiDialogTitle,
      MuiDialogActions,
      MuiDialogContent,
      MuiPaper,
      MuiSwitch,
      MuiAutocomplete,
      MuiAvatarGroup,
      MuiCardContent,
      MuiDialog,
      MuiPopover,
      MuiTablePagination,
      MuiStepIcon,
      MuiAccordion,
      MuiAccordionSummary,
      MuiAccordionDetails,
    },
  });
}

export const globalStyles = () => ({
  '*': {
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSmooth: 'never',
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
  },
});
